import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, PawPrint, Image as ImgIcon, Share2, Copy, Check, Power } from "lucide-react";

export default function PetGallery() {
  const { petId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => {
    setLoading(true);
    return api.get(`/pets/${petId}/gallery`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Couldn't load gallery"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [petId]);

  const shareUrl = data?.pet?.share_token
    ? `${window.location.origin}/share/pets/${data.pet.share_token}`
    : "";

  const enableShare = async () => {
    try { await api.post(`/pets/${petId}/share`); await load(); toast.success("Share link ready"); }
    catch { toast.error("Couldn't enable share"); }
  };
  const disableShare = async () => {
    if (!window.confirm("Turn sharing off? The public link will stop working.")) return;
    try { await api.delete(`/pets/${petId}/share`); await load(); toast.success("Sharing disabled"); }
    catch { toast.error("Couldn't disable share"); }
  };
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true); setTimeout(() => setCopied(false), 1800);
    toast.success("Link copied");
  };
  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `${data.pet.name}'s PawTrek gallery`, url: shareUrl }); } catch {}
    } else copyLink();
  };

  if (loading) return <div className="p-6 text-sm text-[#6B625B]">Loading…</div>;
  if (!data) return null;

  const photos = data.photos || [];
  // Pair before/after taken within 4 hours of each other
  const pairs = [];
  const singles = [];
  const used = new Set();
  const sorted = [...photos].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  for (let i = 0; i < sorted.length; i++) {
    if (used.has(i)) continue;
    const p = sorted[i];
    if (p.photo_label === "before") {
      const partner = sorted.findIndex((q, j) =>
        j > i && !used.has(j) && q.photo_label === "after" &&
        (new Date(q.created_at) - new Date(p.created_at)) < 4 * 3600 * 1000
      );
      if (partner !== -1) {
        pairs.push({ before: p, after: sorted[partner] });
        used.add(i); used.add(partner);
        continue;
      }
    }
    singles.push(p);
    used.add(i);
  }

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6">
      <header>
        <button onClick={() => nav(-1)} className="text-sm text-[#6B625B] flex items-center gap-1 mb-3">
          <ArrowLeft className="w-4 h-4"/> Back
        </button>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {data.pet.photo_url ? (
              <img src={data.pet.photo_url} alt={data.pet.name} className="w-16 h-16 rounded-2xl object-cover"/>
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-[#F5E6CF] grid place-content-center text-[#A9480F]">
                <PawPrint className="w-7 h-7"/>
              </div>
            )}
            <div className="min-w-0">
              <div className="label-accent">Gallery</div>
              <h1 className="font-display text-3xl font-bold mt-1">{data.pet.name}</h1>
              <div className="text-sm text-[#6B625B]">{data.pet.breed} · {photos.length} photo{photos.length===1?"":"s"}</div>
            </div>
          </div>
          <Button onClick={() => setShareOpen(true)}
            className="rounded-full bg-[#2D4A3E] hover:bg-[#21382E] btn-tactile"
            data-testid="btn-share-open">
            <Share2 className="w-4 h-4 mr-1"/> Share
          </Button>
        </div>
      </header>

      {photos.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#E5DCCE] p-10 text-center text-[#6B625B]">
          <ImgIcon className="w-8 h-8 mx-auto mb-2 opacity-40"/>
          <p className="text-sm">No photos yet — ask your groomer to snap a before-and-after next visit!</p>
        </div>
      ) : (
        <div className="space-y-6">
          {pairs.length > 0 && (
            <section>
              <h2 className="font-display text-xl font-bold mb-3">Before & after</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {pairs.map((p, i) => (
                  <div key={i} className="bg-white rounded-3xl border border-[#E5DCCE] p-3 shadow-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <ClickableImg photo={p.before} onOpen={() => setOpenIdx({ src: p.before, list: photos })} tint="amber"/>
                      <ClickableImg photo={p.after} onOpen={() => setOpenIdx({ src: p.after, list: photos })} tint="emerald"/>
                    </div>
                    <div className="text-[11px] text-[#6B625B] mt-2 text-center">
                      {new Date(p.after.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {singles.length > 0 && (
            <section>
              <h2 className="font-display text-xl font-bold mb-3">All photos</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {singles.map((p) => (
                  <ClickableImg key={p.id} photo={p}
                    onOpen={() => setOpenIdx({ src: p, list: photos })}
                    tint={p.photo_label === "before" ? "amber" : p.photo_label === "after" ? "emerald" : "neutral"}/>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <Dialog open={!!openIdx} onOpenChange={() => setOpenIdx(null)}>
        <DialogContent className="max-w-3xl bg-transparent border-0 shadow-none p-0">
          {openIdx && (
            <img src={`${API}${openIdx.src.photo_url.replace('/api','')}`}
              alt="" className="w-full h-auto rounded-2xl"/>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#C85A17]"/> Share {data.pet.name}'s gallery
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!data.pet.share_enabled ? (
              <>
                <p className="text-sm text-[#6B625B]">
                  Turn on sharing to get a public read-only link. Anyone with the link can view {data.pet.name}'s photos —
                  no PawTrek account needed. Great for socials, family, or your favourite pack parent.
                </p>
                <Button onClick={enableShare}
                  className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile"
                  data-testid="btn-share-enable">
                  <Share2 className="w-4 h-4 mr-2"/> Create share link
                </Button>
              </>
            ) : (
              <>
                <div className="rounded-2xl bg-[#F5E6CF] border border-amber-900/10 p-3">
                  <div className="label-accent text-[#A9480F]">Public link</div>
                  <div className="font-mono text-xs break-all mt-1" data-testid="share-url">{shareUrl}</div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={copyLink} className="flex-1 rounded-full bg-[#C85A17] hover:bg-[#A9480F]"
                    data-testid="btn-share-copy">
                    {copied ? <Check className="w-4 h-4 mr-2"/> : <Copy className="w-4 h-4 mr-2"/>}
                    {copied ? "Copied!" : "Copy link"}
                  </Button>
                  {navigator.share && (
                    <Button onClick={shareNative} variant="outline" className="rounded-full"
                      data-testid="btn-share-native">
                      <Share2 className="w-4 h-4"/>
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`Check out ${data.pet.name}'s grooming gallery!`)}`}
                    target="_blank" rel="noreferrer"
                    className="pill bg-black text-white hover:opacity-80 transition-opacity"
                    data-testid="share-x">Share on X</a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank" rel="noreferrer"
                    className="pill bg-[#1877F2] text-white hover:opacity-80 transition-opacity">Facebook</a>
                  <a href={`sms:?&body=${encodeURIComponent(`Check out ${data.pet.name}'s gallery: ${shareUrl}`)}`}
                    className="pill bg-emerald-600 text-white hover:opacity-80 transition-opacity">Text</a>
                </div>
                <Button onClick={disableShare} variant="outline"
                  className="w-full rounded-full text-rose-600 hover:bg-rose-50"
                  data-testid="btn-share-disable">
                  <Power className="w-4 h-4 mr-2"/> Turn off sharing
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClickableImg({ photo, onOpen, tint }) {
  const tintClass = {
    amber: "bg-amber-200 text-amber-900",
    emerald: "bg-emerald-200 text-emerald-900",
    neutral: "bg-stone-200 text-stone-800",
  }[tint];
  return (
    <button onClick={onOpen}
      className="relative aspect-square rounded-2xl overflow-hidden bg-[#F3EDE4] card-tactile group"
      data-testid={`photo-${photo.id}`}>
      <img src={`${API}${photo.photo_url.replace('/api','')}`} alt=""
        className="w-full h-full object-cover"/>
      {photo.photo_label && (
        <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${tintClass}`}>
          {photo.photo_label}
        </span>
      )}
    </button>
  );
}
